import type { Schema } from '@atlaskit/editor-prosemirror/model';

export interface SchemaValidator {
	/**
	 * Get the name of the top node type
	 */
	getTopNodeType: () => string;

	/**
	 * Check if a node type is a leaf node (cannot have content)
	 */
	isLeafNode: (nodeType: string) => boolean;

	/**
	 * Create a stateful validator for a parent node's content
	 * Returns a function that checks each child sequentially and advances the content match state
	 *
	 * Usage:
	 *   const validator = schema.isValidChild('listItem')
	 *   validator('paragraph')  // true, advances to position 1
	 *   validator('bulletList') // true (now valid after paragraph)
	 */
	isValidChild: (parentType?: string) => (childType: string) => boolean;

	/**
	 * Check if a mark type exists in the schema
	 */
	isValidMarkType: (markType: string) => boolean;

	/**
	 * Check if a node type exists in the schema
	 */
	isValidNodeType: (nodeType: string) => boolean;
}

// ADF Schema Validator using ProseMirror Schema
export class AdfSchemaValidator implements SchemaValidator {
	private readonly nodes: Set<string>;
	private readonly marks: Set<string>;

	constructor(private readonly schema: Schema) {
		this.nodes = new Set(Object.keys(schema.nodes));
		this.marks = new Set(Object.keys(schema.marks))
	}

	isValidChild(parentType?: string): (childType: string) => boolean {
		const resolvedParentType = parentType || this.getTopNodeType();
		const $parentNodeType = this.schema.nodes[resolvedParentType];

		if (!$parentNodeType || !$parentNodeType.contentMatch) {
			return () => true;
		}

		// Closure maintains contentMatch state
		let currentMatch = $parentNodeType.contentMatch;

		return (childType: string) => {
			const childNodeType = this.schema.nodes[childType];
			if (!childNodeType) {
				return false;
			}

			const nextMatch = currentMatch.matchType(childNodeType);
			if (nextMatch) {
				currentMatch = nextMatch;
				return true;
			}

			return false;
		};
	}

	isLeafNode(nodeType: string): boolean {
		const $nodeType = this.schema.nodes[nodeType];
		return $nodeType ? $nodeType.isLeaf : false;
	}

	getTopNodeType(): string {
		return this.schema.topNodeType?.name || 'doc';
	}

	isValidNodeType(nodeType: string): boolean {
		return this.nodes.has(nodeType);
	}

	isValidMarkType(markType: string): boolean {
		return this.marks.has(markType);
	}
}
